// public/config.js

// --- API and Service Endpoints ---
export const SCRYFALL_API_COLLECTION = 'https://api.scryfall.com/cards/collection';
export const SCRYFALL_API_DECK_EXPORT = 'https://api.scryfall.com/decks/{deck_id}/export/json';

// --- Binder and Page Layout ---
export const SEARCH_RESULTS_PER_PAGE = 40;
export const CARDS_PER_PAGE_FACE = 9;
export const MAX_CARDS_PER_FETCH = 70;
export const SIDEWAYS_LAYOUTS = ['split', 'planar', 'scheme', 'vanguard', 'aftermath'];
export const FLIPPABLE_LAYOUTS = [
  'transform',
  'modal_dfc',
  'reversible_card',
  'double_faced_token',
  'art_series',
];

// --- UI and Interaction ---
export const HOVER_DELAY = 1000;
export const MD_BREAKPOINT = 768; // Corresponds to Tailwind's 'md' breakpoint
export const SWIPE_THRESHOLD = 50;
export const KEYBOARD_FLIP_INTERVAL = 175; // ms, for ~5.7 flips per second

// --- Want page People tab: the representative few cards per person ---
// (VISUALS.md "A list of X shows a representative few of what each X holds")
export const PEOPLE_CARDS_DESKTOP = 8;
// A phone shows two full rows of its 3-across grid.
export const PEOPLE_PHONE_COLUMNS = 3;
export const PEOPLE_PHONE_ROWS = 2;
export const PEOPLE_CARDS_PHONE = PEOPLE_PHONE_COLUMNS * PEOPLE_PHONE_ROWS;

// --- Want page Wants tab ---
// Rows per page, as Scryfall: 175 in its checklist, 60 in its image grid.
export const WANT_PAGE_SIZES = Object.freeze({ list: 175, cards: 60 });
// The Add a card row's typing pause before asking for suggestions, so a word
// typed fast is one request.
export const ADD_CARD_PAUSE_MS = 160;
// How long a suggestion lookup may take before the row says "Searching…"
// (a fast one never flickers it; a slow closest-spelling one is not silent).
export const ADD_CARD_SLOW_MS = 400;

// --- Sorting and Data Categories ---
export const COLOR_CATEGORY_VALUES = {
  W: 0,
  U: 1,
  B: 2,
  R: 3,
  G: 4,
  Multicolor: 5,
  Artifact: 6,
  Colorless: 7,
  NonDeck: 8,
  Land: 9,
  NotFound: 99,
};
export const LAND_SORT_VALUES = { NonBasic: 0, SnowBasic: 1, Basic: 2, Other: 9 };
export const REVISED_NON_DECK_TYPES = [
  'token',
  'emblem',
  'scheme',
  'plane',
  'vanguard',
  'conspiracy',
  'hero',
  'art_series',
];
export const COLOR_TAB_DISPLAY_NAMES = {
  W: 'White',
  U: 'Blue',
  B: 'Black',
  R: 'Red',
  G: 'Green',
  Multicolor: 'Multi',
  Artifact: 'Artifact',
  Colorless: 'Colorless',
  Land: 'Land',
};

export const COLOR_TAB_ORDER = ['W', 'U', 'B', 'R', 'G', 'Multicolor', 'Colorless'];

export const COLOR_TAB_STYLES = {
  W: { bg: '#f9faf4', text: '#92702e', label: 'W', svg: '/mana/W.svg' },
  U: { bg: '#0e68ab', text: '#ffffff', label: 'U', svg: '/mana/U.svg' },
  B: { bg: '#2b2630', text: '#c0b7c9', label: 'B', svg: '/mana/B.svg' },
  R: { bg: '#d3202a', text: '#ffffff', label: 'R', svg: '/mana/R.svg' },
  G: { bg: '#00733e', text: '#ffffff', label: 'G', svg: '/mana/G.svg' },
  Multicolor: { bg: '#e8c547', text: '#3d3200', label: 'M', svg: '/mana/M.svg' },
  Colorless: { bg: '#c4c0b6', text: '#3d3a32', label: 'C', svg: '/mana/C.svg' },
};

// --- Styling and Appearance ---
export const BINDER_DARKEN_FACTOR = 0.8; // 20% darker for borders/stitches
export const CARD_ASPECT_RATIO = 63 / 88;

// --- Search ---
export const SEARCH_SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name A\u2013Z' },
  { value: 'name-desc', label: 'Name Z\u2013A' },
  { value: 'price-desc', label: 'Price High\u2013Low' },
  { value: 'price-asc', label: 'Price Low\u2013High' },
  { value: 'quantity-desc', label: 'Most Copies' },
  { value: 'set-desc', label: 'Set (Newest)' },
];

export const SEARCH_RARITY_OPTIONS = ['common', 'uncommon', 'rare', 'mythic'];
export const SEARCH_FINISH_OPTIONS = ['nonfoil', 'foil', 'etched'];
export const SEARCH_FRAME_OPTIONS = [
  { value: '1993', label: 'Old Border' },
  { value: '2003', label: 'Modern' },
  { value: '2015', label: 'Modern (2015+)' },
];

// --- Firebase ---
// The project's Cloud Storage bucket (firebase.js config, set symbol URLs).
export const FIREBASE_STORAGE_BUCKET = 'binder-browser.firebasestorage.app';

// --- Discord ---
export const DISCORD_BOT_CLIENT_ID = '1480087138040283268';

// --- URL and Caching ---
// The one short-URL codec: code -> { source, type }, entry-for-entry the
// server's binder_urls.CODE_MAP. Parsing ('/mfb.abc') and minting
// (url-parser.toShortPath) both read this table, so a new source is one line.
export const SHORT_CODES = {
  mfd: { source: 'moxfield', type: 'deck' },
  mfb: { source: 'moxfield', type: 'binder' },
  mfc: { source: 'moxfield', type: 'collection' },
  ad: { source: 'archidekt', type: 'deck' },
  ac: { source: 'archidekt', type: 'collection' },
  sf: { source: 'scryfall', type: 'deck' },
  cc: { source: 'cubecobra', type: 'cube' },
  mbd: { source: 'manabox', type: 'deck' },
};
// The codes that open as a binder page, with their source URL prefix. A cube
// (cc) never opens as a binder (url-parser.extractBinderSourceInfo).
export const PATH_CODE_MAP = {
  mfd: { ...SHORT_CODES.mfd, urlPrefix: 'https://moxfield.com/decks/' },
  mfb: { ...SHORT_CODES.mfb, urlPrefix: 'https://moxfield.com/binders/' },
  mfc: { ...SHORT_CODES.mfc, urlPrefix: 'https://moxfield.com/collection/' },
  sf: { ...SHORT_CODES.sf, urlPrefix: 'https://scryfall.com/@binder-browser/decks/' },
  ad: { ...SHORT_CODES.ad, urlPrefix: 'https://archidekt.com/decks/' },
  ac: { ...SHORT_CODES.ac, urlPrefix: 'https://archidekt.com/collection/' },
  mbd: { ...SHORT_CODES.mbd, urlPrefix: 'https://manabox.app/decks/' },
};
export const RECENT_BINDERS_KEY = 'virtualBinderRecentBinders_v1';
export const MAX_RECENT_BINDERS = 8;
export const SELECTED_CARDS_CACHE_PREFIX = 'virtualBinderSelectedCards_v1_';
export const PINNED_BINDERS_KEY = 'pinnedBinders';
export const MY_BINDERS_KEY = 'virtualBinderMyBinders_v1';
// Signed-in home page list, cached for instant paint: { uid, binders }.
export const HOME_BINDER_LIST_KEY = 'homeBinderList_v1';
export const MAX_MY_BINDERS = 3;

// --- Binder image loading ---
// A load tier that has not drained within this budget stops blocking the tiers
// behind it (one hung Scryfall request must not stall the whole buffer).
export const IMAGE_TIER_MAX_WAIT_MS = 4000;
